import {
  DestroyRef,
  Injectable,
  PLATFORM_ID,
  inject,
  signal,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HOTKEYS_CONFIG } from './hotkeys.config';
import type {
  HotkeyBinding,
  HotkeyHelpEntry,
  HotkeyRegistration,
} from './hotkeys.types';
import {
  combosMatch,
  eventToCombo,
  isEditableTarget,
  isIgnoredTag,
  normalizeCombos,
} from './hotkeys.utils';

@Injectable({ providedIn: 'root' })
export class HotkeysService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly config = inject(HOTKEYS_CONFIG);
  private readonly destroyRef = inject(DestroyRef);

  private readonly bindings = new Map<string, HotkeyBinding>();
  private readonly _enabled = signal(true);
  private readonly _activeScopes = signal<string[]>(['global']);
  private readonly _registryVersion = signal(0);

  readonly enabled = this._enabled.asReadonly();
  readonly activeScopes = this._activeScopes.asReadonly();
  readonly registryVersion = this._registryVersion.asReadonly();

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const handler = (event: KeyboardEvent) => this.handleKeydown(event);
    this.document.addEventListener('keydown', handler, { capture: true });
    this.destroyRef.onDestroy(() => {
      this.document.removeEventListener('keydown', handler, { capture: true });
    });
  }

  /** Register or replace a hotkey binding. */
  register(registration: HotkeyRegistration): void {
    const scope = registration.scope ?? 'global';
    const binding: HotkeyBinding = {
      ...registration,
      scope,
      combos: normalizeCombos(
        registration.combo,
        this.config.metaAsCtrlOnMac ?? true,
      ),
    };
    this.bindings.set(registration.id, binding);
    this._registryVersion.update((v) => v + 1);
  }

  unregister(id: string): void {
    if (!this.bindings.delete(id)) {
      return;
    }
    this._registryVersion.update((v) => v + 1);
  }

  has(id: string): boolean {
    return this.bindings.has(id);
  }

  /** Enable or disable all hotkeys (e.g. during modal or tour). */
  setEnabled(enabled: boolean): void {
    this._enabled.set(enabled);
  }

  /**
   * Set page/feature scopes. Always merged with `alwaysActiveScopes` from config.
   * Example: `setScopes(['atm-stats'])` → active: global + atm-stats.
   */
  setScopes(scopes: string[]): void {
    const always = this.config.alwaysActiveScopes ?? ['global'];
    this._activeScopes.set([...new Set([...always, ...scopes])]);
  }

  /** Bindings for active scopes, sorted by priority (desc). */
  getActiveBindings(): HotkeyBinding[] {
    const scopes = new Set(this._activeScopes());
    return [...this.bindings.values()]
      .filter((binding) => this.bindingInScope(binding, scopes))
      .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  }

  /** Help entries for UI (one row per combo). */
  getHelpEntries(scope?: string): HotkeyHelpEntry[] {
    const scopes = scope
      ? new Set([scope, ...(this.config.alwaysActiveScopes ?? ['global'])])
      : new Set(this._activeScopes());

    const entries: HotkeyHelpEntry[] = [];
    for (const binding of this.bindings.values()) {
      if (!this.bindingInScope(binding, scopes)) {
        continue;
      }
      for (const combo of binding.combos) {
        entries.push({
          id: binding.id,
          combo,
          description: binding.description,
          scope: binding.scope,
        });
      }
    }
    return entries.sort((a, b) => a.combo.localeCompare(b.combo));
  }

  private handleKeydown(event: KeyboardEvent): void {
    if (!this._enabled() || event.defaultPrevented || event.isComposing) {
      return;
    }

    const eventCombo = eventToCombo(event, this.config.metaAsCtrlOnMac ?? true);
    const editable = isEditableTarget(event.target);
    const ignoreTags = this.config.ignoreTags ?? [];

    for (const binding of this.getActiveBindings()) {
      if (binding.when && !binding.when()) {
        continue;
      }

      if (
        !binding.allowInInputs &&
        (editable || isIgnoredTag(event.target, ignoreTags))
      ) {
        continue;
      }

      const matched = binding.combos.some((combo) =>
        combosMatch(eventCombo, combo, this.config.metaAsCtrlOnMac ?? true),
      );
      if (!matched) {
        continue;
      }

      const result = binding.handler();
      if (result !== false) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }
  }

  private bindingInScope(
    binding: HotkeyBinding,
    scopes: Set<string>,
  ): boolean {
    const bindingScopes = Array.isArray(binding.scope)
      ? binding.scope
      : [binding.scope ?? 'global'];
    return bindingScopes.some((scope) => scopes.has(scope));
  }
}
